import type { MemberIds } from '@amumin/api-client';
import { Avatar, cx, Icon, memberStyle } from '@amumin/ui-core';

import type { MemberLite } from '../lib/agenda.js';
import { appText } from '../state/session.js';

export interface MemberPickerProps {
  id: string;
  label: string;
  members: ReadonlyArray<MemberLite>;
  value: MemberIds;
  onChange: (value: MemberIds) => void;
  /** Optie "Iedereen" (afspraken); taken kiezen altijd expliciet leden. */
  allowEveryone?: boolean;
  /** Tekst op de chip "Iedereen" (herinneringen: "Alle kinderen"). */
  everyoneLabel?: string | undefined;
  error?: string | undefined;
  /** Eén lid tegelijk (bv. "wie rijdt"). */
  single?: boolean;
}

/**
 * Ledenkiezer als grote chips met gezichtje en naam in de lidtint (DESIGN.md §3.2, kleur =
 * persoon); meerdere leden tegelijk. Echte checkboxen, dus toetsenbord en schermlezer werken.
 */
export function MemberPicker({
  id,
  label,
  members,
  value,
  onChange,
  allowEveryone = false,
  everyoneLabel,
  error,
  single = false,
}: MemberPickerProps) {
  const a = appText.value;
  const everyone = value === 'everyone';
  // Bij "Iedereen" staat alleen die chip aan; een persoon aantikken kiest dan alleen die persoon.
  const selected = new Set(everyone ? [] : value);

  const toggle = (memberId: string) => {
    // Vanuit "Iedereen" betekent een tik op iemand: alleen die persoon (zoals je verwacht).
    if (single || everyone) {
      onChange([memberId]);
      return;
    }
    const next = new Set(value);
    if (next.has(memberId)) next.delete(memberId);
    else next.add(memberId);
    onChange(members.filter((m) => next.has(m.id)).map((m) => m.id));
  };

  return (
    <fieldset class="member-picker" id={id} aria-describedby={error ? `${id}-error` : undefined}>
      <legend class="field-label">{label}</legend>
      <div class="member-picker-options">
        {allowEveryone ? (
          <label
            class={cx('member-chip', everyone && 'is-checked')}
            data-checked={everyone ? 'true' : 'false'}
          >
            <input
              type="checkbox"
              name={`${id}-everyone`}
              checked={everyone}
              onChange={() => onChange(everyone ? [] : 'everyone')}
            />
            <span class="member-chip-family" aria-hidden="true">
              <Icon id="users" size={18} />
            </span>
            <span>{everyoneLabel ?? a.common.everyone}</span>
          </label>
        ) : null}
        {members.map((member) => {
          const checked = selected.has(member.id);
          return (
            <label
              key={member.id}
              class={cx('member-chip', checked && 'is-checked')}
              data-checked={checked ? 'true' : 'false'}
              data-member={member.id}
              style={memberStyle(member.color_key)}
            >
              <input
                type={single ? 'radio' : 'checkbox'}
                name={single ? id : `${id}-${member.id}`}
                checked={checked}
                onChange={() => toggle(member.id)}
              />
              <Avatar
                name={member.display_name}
                colorKey={member.color_key}
                icon={member.avatar_icon}
                size="chip"
                decorative
              />
              <span>{member.display_name}</span>
              {checked ? <Icon id="check" size={16} class="member-chip-check" /> : null}
            </label>
          );
        })}
      </div>
      {error ? (
        <span id={`${id}-error`} class="field-error" role="alert">
          {error}
        </span>
      ) : null}
    </fieldset>
  );
}

/** Kleine gezichtjes van een lijst leden (of één "Iedereen"-chip). */
export function MemberAvatars({
  memberIds,
  members,
  max = 4,
}: {
  memberIds: MemberIds;
  members: ReadonlyArray<MemberLite>;
  max?: number;
}) {
  const a = appText.value;
  // Fixer golf 6b r1: alleen wonen = geen "Iedereen"-chip (en geen eigen gezichtje erbij).
  if (members.length <= 1) return null;
  if (memberIds === 'everyone' || (memberIds.length > 1 && memberIds.length >= members.length)) {
    return (
      <span class="avatar-stack" role="img" aria-label={a.common.everyone}>
        <span class="member-chip-family" aria-hidden="true">
          <Icon id="users" size={16} />
        </span>
      </span>
    );
  }
  const byId = new Map(members.map((x) => [x.id, x]));
  const list = memberIds.map((id) => byId.get(id)).filter((x): x is MemberLite => Boolean(x));
  const shown = list.slice(0, max);
  return (
    <span class="avatar-stack">
      {shown.map((member) => (
        <Avatar
          key={member.id}
          name={member.display_name}
          colorKey={member.color_key}
          icon={member.avatar_icon}
          size="chip"
        />
      ))}
      {list.length > shown.length ? (
        <span class="avatar-more">+{list.length - shown.length}</span>
      ) : null}
    </span>
  );
}
